// Sorting + finish-badge helpers shared by the Inventory, Price List, Quick Price
// Lookup, and the "Add Products" picker. Single source of truth so product
// ordering is identical everywhere.

// Extract diameter from description (e.g. '2 1/2"' or '1-5/8"' → number)
const getDiameter = (desc) => {
  if (!desc) return 999;
  // Try to find pattern like 2-1/2", 1 5/8", 6-5/8", etc.
  const match = desc.match(/(\d+)[\s-]?(\d+)?\/?(\d+)?["']/);
  if (!match) return 999;
  const whole = parseInt(match[1]) || 0;
  const num = parseInt(match[2]) || 0;
  const den = parseInt(match[3]) || 1;
  return whole + (num > 0 ? num / den : 0);
};

// Extract length from description (e.g. "x 21' Tubing" → 21, "x 10'6\"" → 10.5)
const getLength = (desc) => {
  if (!desc) return 0;
  const match = desc.match(/x\s*(\d+)'(?:(\d+)")?/i);
  if (!match) return 0;
  const feet = parseInt(match[1]) || 0;
  const inches = parseInt(match[2]) || 0;
  return feet + (inches / 12);
};

// Extract fabric height (e.g. "x 3'" → 3, "x 42\"" → 3.5)
const getFabricHeight = (desc) => {
  if (!desc) return 0;
  const ftMatch = desc.match(/x\s*(\d+(?:\.\d+)?)'/i);
  if (ftMatch) return parseFloat(ftMatch[1]);
  const inMatch = desc.match(/x\s*(\d+)"/i);
  if (inMatch) return parseInt(inMatch[1]) / 12;
  return 0;
};

// Detect fabric grade. Residential: 11-1/2 gauge | Commercial: 9 gauge
const getFabricGrade = (desc) => {
  if (!desc) return 'unknown';
  if (/11[\s-]?1\/2\s*Gauge/i.test(desc)) return 'residential';
  if (/9\s*Gauge/i.test(desc)) return 'commercial';
  if (/12\.5/i.test(desc)) return 'temporary'; // CRTW temporary fence
  return 'unknown';
};

// Detect finish — Galvanized or Black
const getFinish = (desc) => {
  if (!desc) return 'galvanized';
  return desc.toLowerCase().includes('black') ? 'black' : 'galvanized';
};

// CHAIN LINK FABRIC — grade, then finish (galv first), then height ascending
const sortChainLink = (a, b) => {
  const gradeOrder = { residential: 1, commercial: 2, temporary: 3, unknown: 4 };
  const gradeDiff = (gradeOrder[getFabricGrade(a.description)] || 99) - (gradeOrder[getFabricGrade(b.description)] || 99);
  if (gradeDiff !== 0) return gradeDiff;

  const aFinish = getFinish(a.description);
  const bFinish = getFinish(b.description);
  if (aFinish !== bFinish) return aFinish === 'galvanized' ? -1 : 1;

  return getFabricHeight(a.description) - getFabricHeight(b.description);
};

// PIPE — diameter ascending, then length
const sortPipe = (a, b) => {
  const aDia = getDiameter(a.description);
  const bDia = getDiameter(b.description);
  if (aDia !== bDia) return aDia - bDia;
  return getLength(a.description) - getLength(b.description);
};

// FITTINGS — by type, then by diameter
const FITTING_TYPE_ORDER = {
  'tension band': 1,
  'tension bar': 2,
  'brace band': 3,
  'rail end': 4,
  'post cap': 5,
  'loop cap': 6,
  'boulevard': 7,
  'barb arm': 8,
  'hog ring': 9,
  'fence tie': 10,
  'carriage bolt': 11,
  'cantilever': 12,
  'hinge': 13,
  'fork': 14,
  'collar': 15,
  'drop rod': 16,
  'ild': 17,
};

const getFittingTypeRank = (desc) => {
  if (!desc) return 99;
  const lower = desc.toLowerCase();
  for (const [keyword, rank] of Object.entries(FITTING_TYPE_ORDER)) {
    if (lower.includes(keyword)) return rank;
  }
  return 98;
};

const sortFittings = (a, b) => {
  const aRank = getFittingTypeRank(a.description);
  const bRank = getFittingTypeRank(b.description);
  if (aRank !== bRank) return aRank - bRank;

  const aDia = getDiameter(a.description);
  const bDia = getDiameter(b.description);
  if (aDia !== bDia) return aDia - bDia;

  return (a.description || '').localeCompare(b.description || '');
};

// WOOD — by dimensions (1x4x4 → 1x4x6 → 1x6x6 …)
const parseWoodDimensions = (desc) => {
  if (!desc) return [99, 99, 99];
  const match = desc.match(/(\d+)\s*x\s*(\d+)\s*x\s*(\d+)/i);
  if (match) return [parseInt(match[1]) || 0, parseInt(match[2]) || 0, parseInt(match[3]) || 0];
  return [99, 99, 99];
};

const sortWood = (a, b) => {
  const [a1, a2, a3] = parseWoodDimensions(a.description);
  const [b1, b2, b3] = parseWoodDimensions(b.description);
  if (a1 !== b1) return a1 - b1;
  if (a2 !== b2) return a2 - b2;
  if (a3 !== b3) return a3 - b3;
  return (a.description || '').localeCompare(b.description || '');
};

// PVC / ALUMINUM — panels → posts → gates → hardware → misc
const PVC_ALUM_TYPE_ORDER = {
  'panel': 1,
  'section': 1,
  'post': 2,
  'gate': 3,
  'hinge': 4,
  'latch': 4,
  'hardware': 4,
  'cap': 4,
  'bracket': 4,
};

const getPvcAlumTypeRank = (desc) => {
  if (!desc) return 99;
  const lower = desc.toLowerCase();
  for (const [keyword, rank] of Object.entries(PVC_ALUM_TYPE_ORDER)) {
    if (lower.includes(keyword)) return rank;
  }
  return 5; // misc/other
};

const sortPvcAlum = (a, b) => {
  const aRank = getPvcAlumTypeRank(a.description);
  const bRank = getPvcAlumTypeRank(b.description);
  if (aRank !== bRank) return aRank - bRank;
  return (a.description || '').localeCompare(b.description || '');
};

// GATES — height ascending, then width
const getGateHeight = (desc) => {
  if (!desc) return 999;
  const match = desc.match(/(\d+(?:\.\d+)?)['′]/);
  return match ? parseFloat(match[1]) : 999;
};

const getGateWidth = (desc) => {
  if (!desc) return 0;
  const matches = desc.match(/(\d+(?:\.\d+)?)['′]/g);
  if (matches && matches.length >= 2) return parseFloat(matches[1]);
  return 0;
};

const sortGates = (a, b) => {
  const aHeight = getGateHeight(a.description);
  const bHeight = getGateHeight(b.description);
  if (aHeight !== bHeight) return aHeight - bHeight;
  return getGateWidth(a.description) - getGateWidth(b.description);
};

const alphabetical = (a, b) => (a.description || '').localeCompare(b.description || '');

// Returns the comparator for a given category.
const comparatorFor = (category) => {
  switch (category) {
    case 'Chain Link': return sortChainLink;
    case 'Galv Pipe':
    case 'Black Pipe': return sortPipe;
    case 'Galv Fittings':
    case 'Black Fittings': return sortFittings;
    case 'Wood':
    case 'Wood Hardware': return sortWood;
    case 'PVC':
    case 'Aluminum': return sortPvcAlum;
    case 'Galv Gates Residential':
    case 'Galv Gates Commercial':
    case 'Black Gates Residential':
    case 'Black Gates Commercial':
    case 'Cantilever Gate': return sortGates;
    default: return alphabetical;
  }
};

// MAIN — sort a single category's products with the right comparator.
// Manual ordering takes priority: when BOTH products being compared have a
// numeric `sortOrder`, they sort by it ascending; otherwise we fall back to the
// category's domain comparator. With no `sortOrder` present anywhere (today's
// state) this is byte-for-byte identical to the previous behavior. This wraps
// EVERY per-category path (including the default alphabetical), and because
// sortProductsFlat() delegates here, the picker/PriceList/QuickLookup inherit it.
export const sortProductsByCategory = (products, category) => {
  const base = comparatorFor(category);
  const withSortOrder = (a, b) =>
    (typeof a.sortOrder === 'number' && typeof b.sortOrder === 'number')
      ? a.sortOrder - b.sortOrder
      : base(a, b);
  return [...products].sort(withSortOrder);
};

// Display order for category sections (logical grouping by material type).
export const CATEGORY_DISPLAY_ORDER = [
  'Chain Link',
  'Galv Pipe',
  'Black Pipe',
  'Galv Fittings',
  'Black Fittings',
  'Aluminum',
  'PVC',
  'Wood',
  'Wood Hardware',
  'Split Rail',
  'Tools/Misc',
];

// Sort a flat, possibly multi-category list: group by category, sort each group
// with its own comparator, then concatenate in CATEGORY_DISPLAY_ORDER (any
// categories not in that list are appended alphabetically). Reuses the same
// per-category logic — no duplication.
export const sortProductsFlat = (products) => {
  const groups = {};
  products.forEach(p => {
    const cat = (p.category || '').trim();
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push(p);
  });

  const orderedCats = [
    ...CATEGORY_DISPLAY_ORDER.filter(c => groups[c]),
    ...Object.keys(groups).filter(c => !CATEGORY_DISPLAY_ORDER.includes(c)).sort(),
  ];

  return orderedCats.flatMap(cat => sortProductsByCategory(groups[cat], cat));
};

// Finish badge for a product — { label, color, bg, border } or null.
export const getFinishBadge = (product) => {
  const desc = (product.description || '').toLowerCase();
  const cat = (product.category || '').toLowerCase();

  if (desc.includes('black') || cat.includes('black')) {
    return { label: 'Blk', color: '#a78bfa', bg: 'rgba(139, 92, 246, 0.15)', border: 'rgba(139, 92, 246, 0.3)' };
  }
  if (cat.includes('galv') || desc.includes('galvanized') || desc.includes('galv')) {
    return { label: 'Galv', color: '#22d3ee', bg: 'rgba(6, 182, 212, 0.15)', border: 'rgba(6, 182, 212, 0.3)' };
  }
  return null; // No badge for finish-agnostic products (PVC, Wood, etc.)
};
